import {CesiumAdapterService} from '../../services/cesiumAdapter21/cesium-adapter.service';
import {FigureMainClass} from './figureMainClass';
import {MapPosition} from '../../models/MapPosition';
import * as turf from '@turf/turf';
import {from, Observable, Subject, timer} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {ObjectConfig} from '../../models/ObjectConfig';

export class Polygon extends FigureMainClass {

  private _path: MapPosition[];
  private _color: string;
  private _opacity: number;
  private _borderId: string;
  private _borderWidth: number;
  private _borderColor: string;


  get borderId() {
    return this._borderId;
  }

  set borderId(val) {
    this._borderId = val;
  }

  get path() {
    return this._path;
  }

  set path(val) {
    this._path = val;
    this.updateLinkedObjectsAnchorPosition();
    this.debounceObservable$.pipe(debounceTime(300)).subscribe(()=>{
      this.setObjectConfigValue('latLngs', this.path);
    });
  }

  get color() {
    return this._color;
  }

  set color(val) {
    this._color = val;
  }

  get opacity() {
    return this._opacity;
  }

  set opacity(val) {
    this._opacity = val;
  }

  get pathAsArrays() {
    return this.path.map((mp: MapPosition) => mp.asArray());
  }

  get closedPathAsArrays(): number[][] {
    let a = this.pathAsArrays;
    a.push(a[0]);
    return a;
  }

  get closedPath(): MapPosition[] {
    let a = this.path.slice();
    a.push(a[0]);
    return a;
  }

  get borderWidth(): number {
    return this._borderWidth;
  }

  //preview border
  private previewBorder = null;

  get borderColor(): string {
    return this._borderColor;
  }

  __init(config) {
    this._path = config.getPositions();
    this._color = config.fillColor;
    this._opacity = config.getFillOpacity();
    this._borderWidth = parseFloat(config.getStrokeWeight());
    this._borderColor = config.getStrokeColor();
    // note: [SHR]: difference
    this.__updateMainPropertiesFromConfig(config);


    this.showBorderDebounce$.pipe(debounceTime(300)).subscribe(()=>{
      this.view.updateBorderPath(this.path, this.previewBorder, ()=>{
        this.view.setVisibility(this.previewBorder, true);
      });
    })
  }

  protected __onServerUpdate(config) {
    let configPositions = config.getPositions();
    let changed = false;
    let toolChanged = false;

    if (this.tool.id != config.getTool().id){
      // this.removeObject(true);
      this.tool = config.getTool();
      toolChanged = true;
    }
    if (configPositions.length != this.path.length) {
      changed = true;
    }
    if (!changed) {
      for (let i = 0; i < this.path.length; i++) {
        if (!this.path[i].equals(configPositions[i])) {
          changed = true;
          break;
        }
      }
    }
    if (changed) {
      this._path = configPositions;
      this.view.movePolygon(this.viewEntityId, this.closedPath);
      if (this.borderWidth){
        this.view.setVisibility(this.borderId, false);
        this.view.updateBorderPath(this.path, this.borderId, () => {
          this.view.setVisibility(this.borderId, true);
        });
      }
    }

    if (toolChanged){
      if (this._borderWidth){
        this.view.removeObject(this.borderId);
        this.borderId = null;
      }
      this.__updateMainPropertiesFromConfig(config);
        this._updateOptionsOnMap();
    }
  }

  __onInternalUpdate(config){
    console.log('__onInternalUpdate polygon', config);
    let isFullRedraw = false;

    if (this.borderColor != config.getStrokeColor()){
      isFullRedraw = true;
      this._borderColor = config.getStrokeColor();
    }
    if (this.color != config.fillColor){
      isFullRedraw = true;
      this._color = config.fillColor;
    }
    if (this.opacity != config.getImportantFillOpacity()){
      isFullRedraw = true;
      this._opacity = config.getImportantFillOpacity();
    }
    if (isFullRedraw){
      if (this._borderWidth){
        this.view.removeObject(this.borderId);
        this.borderId = null;
      }
      this.__updateMainPropertiesFromConfig(config);
      this._updateOptionsOnMap();
    }
  }

  private __updateMainPropertiesFromConfig(config:ObjectConfig){
    this._color = config.fillColor;
    this._opacity = config.getFillOpacity();
    this._borderWidth = parseFloat(config.getStrokeWeight());
    this._borderColor = config.getStrokeColor();
  }

  public draw() {
    return from(new Promise((resolve, reject) => {
      return this.view.drawPolygon(
        this.tool,
        this,
        ({polygon, border}) => {
          this.setViewEntityId(polygon);
          resolve(polygon);
          this.borderId = border;
        }
      );
    }));
  }

  private _updateOptionsOnMap(){
    this.view.updatePolygonMaterial(this.getViewEntityId(), this);
    if (this.borderWidth && !this.view.isChangingColor){
      this.view.drawPolygonBorder(this.path.concat(this.path[0]), {width: this._borderWidth, opacity: this._borderWidth ? 1 : 0, color: this.color}).then((id)=>{
        this.borderId = id;
      })
    }
    this.view.isChangingColor = false;
  }

  public removeObject() {
    this.view.removeObject(this.borderId);
    super.removeObject();
  }

  public startEdit() {
    this.view.setVisibility(this.borderId, false);
    if(this.previewBorder) {
      this.view.setVisibility(this.previewBorder, false);
    }
    this.view.hideHoverText();
    this.view.editPolygon({entityId: this.viewEntityId, color: this.color}, (id, positions) => {
      this.path = positions;
      if(this.previewBorder){
        this.view.updateBorderPath(this.path, this.previewBorder, () => {
          this.view.setVisibility(this.previewBorder, true);
        });
      }
      this.view.updateBorderPath(this.path, this.borderId, () => {
        this.view.setVisibility(this.borderId, true);
      });
      this.editStop.next(1);
    });
  }

  public startDrag() {
    this.view.startDragPolygon(this.viewEntityId, this.borderId, this.previewBorder);
  }

  public onDragEnd(object: { position, id }) {
    this._dragEnd.next({oldPosition: null, object:object});
    this.path = object.position;
    if(this.previewBorder){
      this.view.updateBorderPath(this.path, this.previewBorder, () => {
        this.view.setVisibility(this.previewBorder, true);
      });
    }
    this.view.updateBorderPath(this.path, this.borderId, () => {
      this.view.setVisibility(this.borderId, true);
    });
    // this.setObjectConfigValue('positions', object.position);
  }

  public setObjectHighlight(flag){
    if (flag && !this.previewBorder){
      this.view.drawHighlightBorder(this.path).then((id)=>{
        this.previewBorder = id;
      })
    }
    if(!flag){
      this.view.removeObject(this.previewBorder, ()=>{
        this.previewBorder = null;
      })
    }
  }

  public setVisibility(val){
    this.view.setVisibility(this.viewEntityId, val);
    this.view.setVisibility(this.borderId, val);
  }

  public rotate(degrees) {
    this.view.setVisibility(this.borderId, false);
    this.path = turf
      .transformRotate(turf.polygon([this.closedPathAsArrays]), degrees)
      .geometry.coordinates[0].map(MapPosition.fromArray)
      .slice(0, -1); // remove closing point

    new Promise((resolve) => {
      this.view.movePolygon(this.viewEntityId, this.closedPath, resolve);
    }).then(() => {
      this.view.updateBorderPath(this.path, this.borderId, () => {
        this.view.setVisibility(this.borderId, true);
      });
    });
  }

  moveWithDirection(event){
    console.log('moveWithDirection');
    this.view.setVisibility(this.borderId, false);
    if (this.previewBorder){
      this.view.setVisibility(this.previewBorder, false);
    }
    let positions = this.path.slice(0);
    this.path = positions.map((pos)=>{
      return this.view.getPositionAfterDirectionOffset(pos, event);
    });
    this.view.movePolygon(this.viewEntityId, this.closedPath);
    this.debounceObservable$.pipe(debounceTime(500)).subscribe(()=>{
      if (this.previewBorder){
        this.view.updateBorderPath(this.path, this.previewBorder, ()=>{
          this.view.setVisibility(this.previewBorder, true);
        });
      }
      this.view.updateBorderPath(this.path, this.borderId, () => {
        this.view.setVisibility(this.borderId, true);
      });
    });
  }

  moveByOffsetFromOriginalPosition(offset){
    this.view.setVisibility(this.borderId, false);
    if (this.previewBorder){
      this.view.setVisibility(this.previewBorder, false);
    }
    let positions = this.path.slice(0);
    positions = positions.map((pos:MapPosition)=>{
      return new MapPosition({lng: pos.longitude+offset.longitude, lat: pos.latitude+offset.latitude});
    });
    this.lastDragPosition = positions;
    this.view.movePolygon(this.viewEntityId, positions);
      if (this.previewBorder){
        this.showBorderDebounce$.next(true);
      }
  }

  public moveBy(offset) {
    const positions = this.path.slice(0);
    positions.map((pos: MapPosition) => {
      pos.longitude += offset.longitude;
      pos.latitude += offset.latitude;
    });
    this.path = positions;
    this.view.updateBorderPath(this.path, this.borderId, () => {
      this.view.setVisibility(this.borderId, true);
    });
    this.view.movePolygon(this.viewEntityId, this.closedPath);
  }

  updateObjectPositionByMultiSelect(){
    if (this.lastDragPosition) {
      this.path = this.lastDragPosition;
      this.view.updateBorderPath(this.path, this.borderId, () => {
        this.view.setVisibility(this.borderId, true);
      });
      // this.view.movePolygon(this.viewEntityId, this.closedPath);
      this.lastDragPosition = null;
    }
  }

  public centerOnObject() {
    this.view.centerOnEntity(this.viewEntityId, this.path[0]);
  }

  public getLinkedObjectsAnchorPosition() {
    let center = null;

    const rectangle = this.view.getRectangleFromPositions(this.path);
    center = Cesium.Rectangle.center(rectangle);
    center.latitude = Cesium.Math.toDegrees(center.latitude);
    center.longitude = Cesium.Math.toDegrees(center.longitude);

    return center;
  }
}
